import { gql } from '@apollo/client';

/** GraphQL documents + helpers shared by the pre-feeds admin list and editor. */

export interface AdminPreFeedAudience {
  intents?: string[] | null;
  careerStages?: string[] | null;
  countryCodes?: string[] | null;
}

export interface AdminPreFeedExclusions {
  signedUpBefore?: string | null;
  maxVotesCast?: number | null;
  maxCommentsWritten?: number | null;
  maxPostsRead?: number | null;
}

export interface AdminPreFeedCriteria {
  minComments?: number | null;
  minVotes?: number | null;
  minEngagement?: number | null;
  maxPosts?: number | null;
}

export interface AdminPreFeed {
  id: string;
  name: string;
  status: string;
  audience?: AdminPreFeedAudience | null;
  exclusions?: AdminPreFeedExclusions | null;
  sourceCriteria?: AdminPreFeedCriteria | null;
  priority: number;
  priorityMode?: string | null;
  publishedAt?: string | null;
  postCount: number;
  audienceCount?: number;
  audienceTotal?: number;
}

export interface AdminPreFeedPost {
  id: string;
  postId: string;
  sortOrder: number;
  enabled: boolean;
  recommendedRank?: number | null;
  post?: {
    id: string;
    title: string;
    slug?: string | null;
    type?: string | null;
    voteScore: number;
    commentCount: number;
    community?: { id?: string; slug: string; name: string } | null;
  } | null;
}

export const PRE_FEED_FIELDS = gql`
  fragment AdminPreFeedFields on PreFeed {
    id
    name
    status
    audience {
      intents
      careerStages
      countryCodes
    }
    exclusions {
      signedUpBefore
      maxVotesCast
      maxCommentsWritten
      maxPostsRead
    }
    sourceCriteria {
      minComments
      minVotes
      minEngagement
      maxPosts
    }
    priority
    priorityMode
    publishedAt
    postCount
    audienceCount
    audienceTotal
  }
`;

export const ADMIN_PRE_FEEDS = gql`
  query AdminPreFeeds {
    adminPreFeeds {
      ...AdminPreFeedFields
    }
  }
  ${PRE_FEED_FIELDS}
`;

export const ADMIN_PRE_FEED = gql`
  query AdminPreFeed($id: ID!) {
    adminPreFeed(id: $id) {
      ...AdminPreFeedFields
      posts {
        id
        postId
        sortOrder
        enabled
        recommendedRank
        post {
          id
          title
          slug
          type
          voteScore
          commentCount
          community {
            id
            slug
            name
          }
        }
      }
    }
  }
  ${PRE_FEED_FIELDS}
`;

export const ADMIN_CREATE_PRE_FEED = gql`
  mutation AdminCreatePreFeed($input: AdminCreatePreFeedInput!) {
    adminCreatePreFeed(input: $input) {
      ...AdminPreFeedFields
    }
  }
  ${PRE_FEED_FIELDS}
`;

export const ADMIN_UPDATE_PRE_FEED = gql`
  mutation AdminUpdatePreFeed($id: ID!, $input: AdminUpdatePreFeedInput!) {
    adminUpdatePreFeed(id: $id, input: $input) {
      ...AdminPreFeedFields
    }
  }
  ${PRE_FEED_FIELDS}
`;

export const ADMIN_SET_PRE_FEED_STATUS = gql`
  mutation AdminSetPreFeedStatus($id: ID!, $status: PreFeedStatus!) {
    adminSetPreFeedStatus(id: $id, status: $status) {
      ...AdminPreFeedFields
    }
  }
  ${PRE_FEED_FIELDS}
`;

export const ADMIN_GENERATE_PRE_FEED_POSTS = gql`
  mutation AdminGeneratePreFeedPosts($preFeedId: ID!) {
    adminGeneratePreFeedPosts(preFeedId: $preFeedId) {
      id
      postCount
    }
  }
`;

export const ADMIN_ADD_PRE_FEED_POST = gql`
  mutation AdminAddPreFeedPost($preFeedId: ID!, $postId: ID!) {
    adminAddPreFeedPost(preFeedId: $preFeedId, postId: $postId) {
      id
      postId
      sortOrder
      enabled
      recommendedRank
    }
  }
`;

export const ADMIN_SAVE_PRE_FEED_POSTS = gql`
  mutation AdminSavePreFeedPosts($preFeedId: ID!, $posts: [PreFeedPostOrderInput!]!) {
    adminSavePreFeedPosts(preFeedId: $preFeedId, posts: $posts) {
      id
    }
  }
`;

export const ADMIN_REMOVE_PRE_FEED_POST = gql`
  mutation AdminRemovePreFeedPost($id: ID!) {
    adminRemovePreFeedPost(id: $id)
  }
`;

export const COMMUNITY_POST_BY_SLUG = gql`
  query PreFeedCommunityPostBySlug($communitySlug: String!, $postSlug: String!) {
    communityPostBySlug(communitySlug: $communitySlug, postSlug: $postSlug) {
      id
      title
    }
  }
`;

export const ADMIN_SEARCH_COMMUNITY_POSTS = gql`
  query PreFeedSearchCommunityPosts($search: String) {
    adminCommunityPosts(search: $search, limit: 10, sortBy: "voteScore", sortDir: "desc") {
      posts {
        id
        title
        slug
        voteScore
        commentCount
        community {
          slug
          name
        }
      }
      total
    }
  }
`;

export const PRIORITY_MODE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'HIGH_AND_NORMAL_FIRST', label: 'High + normal priority posts before this pre-feed (default)' },
  { value: 'HIGH_FIRST', label: 'High before; normal stays in trending after the pre-feed' },
  { value: 'HIGH_FIRST_NORMAL_SPRINKLED', label: 'High before; normal sprinkled into the pre-feed' },
  { value: 'PRE_FEED_FIRST', label: 'Pre-feed first — priority applies only after it' },
];

export const STATUS_PILL_CLASSES: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  LIVE: 'bg-green-100 text-green-800',
  ARCHIVED: 'bg-amber-100 text-amber-800',
};

/** One-line human summary of a feed's audience + exclusions for the list view. */
export function audienceSummary(feed: AdminPreFeed): string {
  const parts: string[] = [];
  const a = feed.audience;
  if (!a || (!a.intents?.length && !a.careerStages?.length && !a.countryCodes?.length)) {
    parts.push('Global fallback');
  } else {
    if (a.intents?.length) parts.push(`intents: ${a.intents.join(', ')}`);
    if (a.careerStages?.length) parts.push(`stages: ${a.careerStages.join(', ')}`);
    if (a.countryCodes?.length) parts.push(`countries: ${a.countryCodes.join(', ')}`);
  }
  const e = feed.exclusions;
  if (e) {
    const ex: string[] = [];
    if (e.signedUpBefore) ex.push(`signed up before ${new Date(e.signedUpBefore).toLocaleDateString()}`);
    if (e.maxVotesCast != null) ex.push(`>${e.maxVotesCast} votes`);
    if (e.maxCommentsWritten != null) ex.push(`>${e.maxCommentsWritten} comments`);
    if (e.maxPostsRead != null) ex.push(`>${e.maxPostsRead} posts read`);
    if (ex.length > 0) parts.push(`excludes: ${ex.join('; ')}`);
  }
  return parts.join(' · ');
}

/** Comma-separated text ⇄ string[] helpers for the audience inputs. */
export function listToText(values?: string[] | null): string {
  return values?.join(', ') ?? '';
}

export function textToList(text: string): string[] {
  return text
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}
